import React, { useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Container, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { Lock, Spa } from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import AppBarContext from 'context/AppBarContext';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import WellbeingCheckinCard from 'components/wellbeing/WellbeingCheckinCard';
import { MY_WELLBEING_SUMMARY } from 'api/graphql/wellbeing';

const DIMENSIONS = ['energy', 'stress', 'rest', 'motivation'];

const toneOf = (score) => {
  if (score === null || score === undefined) return 'text.disabled';
  if (score >= 70) return 'sessionStatus.completed.fg';
  if (score >= 50) return 'sessionStatus.pending.fg';
  return 'sessionStatus.absent.fg';
};

/**
 * Bienestar Corporativo — "Mi bienestar": el histórico PERSONAL del usuario (score actual,
 * dimensiones y tendencia semanal). Es SU dato privado: la empresa solo ve agregados
 * anónimos, y esta pantalla lo dice explícito para construir confianza.
 */
function MyWellbeing() {
  const { t } = useTranslation(['wellbeing', 'routes']);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const pageTitle = t('myTitle', { ns: 'wellbeing', defaultValue: 'Mi bienestar' });

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const { data, loading } = useQuery(MY_WELLBEING_SUMMARY, {
    variables: { weeks: 8 },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });

  const summary = data?.myWellbeingSummary || null;
  const points = useMemo(() => summary?.points || [], [summary]);
  const maxPoint = Math.max(1, ...points.map((p) => p.score || 0));

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <SEOPageTitle title={pageTitle} />

      <WellbeingCheckinCard />

      {loading && !summary && <Skeleton variant="rounded" height={280} sx={{ borderRadius: '16px' }} />}

      {summary && (
        <Grid container spacing={2.5}>
          {/* Score actual */}
          <Grid item xs={12} md={5}>
            <PremiumCard sx={{ height: '100%' }} innerSx={{ height: '100%' }}>
              <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ height: '100%', textAlign: 'center', py: 2 }}>
                <Spa sx={{ color: 'gold.main' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {t('myScore', { ns: 'wellbeing', defaultValue: 'Tu índice de bienestar' })}
                </Typography>
                <Typography sx={{ fontSize: '3.8rem', fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: toneOf(summary.current_score) }}>
                  {summary.current_score ?? '—'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('myScoreHint', { ns: 'wellbeing', defaultValue: 'Promedio de tus últimas 8 semanas (0-100).' })}
                </Typography>
              </Stack>
            </PremiumCard>
          </Grid>

          {/* Dimensiones */}
          <Grid item xs={12} md={7}>
            <PremiumCard sx={{ height: '100%' }} innerSx={{ height: '100%' }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" fontWeight={800}>
                  {t('dimensionsTitle', { ns: 'wellbeing', defaultValue: 'Tus dimensiones' })}
                </Typography>
                {DIMENSIONS.map((dimension) => {
                  const row = (summary.dimensions || []).find((d) => d.dimension === dimension);
                  const score = row?.score ?? null;
                  return (
                    <Stack key={dimension} spacing={0.5}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" fontWeight={700}>
                          {t(`dimensions.${dimension}`, { ns: 'wellbeing', defaultValue: dimension })}
                        </Typography>
                        <Typography variant="caption" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', color: toneOf(score) }}>
                          {score ?? '—'}
                        </Typography>
                      </Stack>
                      <Box sx={{ height: 6, borderRadius: 999, backgroundColor: 'surface.overlay', overflow: 'hidden' }}>
                        <Box
                          sx={{
                            height: '100%',
                            width: '100%',
                            borderRadius: 999,
                            backgroundColor: toneOf(score),
                            transform: `scaleX(${(score || 0) / 100})`,
                            transformOrigin: 'left',
                            transition: 'transform 600ms cubic-bezier(0.32, 0.72, 0, 1)'
                          }}
                        />
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </PremiumCard>
          </Grid>

          {/* Tendencia semanal */}
          <Grid item xs={12}>
            <PremiumCard>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" fontWeight={800}>
                  {t('trendTitle', { ns: 'wellbeing', defaultValue: 'Tu tendencia (8 semanas)' })}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ height: 96 }}>
                  {points.map((point) => (
                    <Stack key={point.week_key} spacing={0.5} alignItems="center" sx={{ flex: 1, minWidth: 0, height: '100%', justifyContent: 'flex-end' }}>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 26,
                          height: `${point.score ? Math.max(8, (point.score / maxPoint) * 100) : 4}%`,
                          borderRadius: '6px 6px 2px 2px',
                          backgroundColor: point.score ? toneOf(point.score) : 'surface.overlay',
                          border: '1px solid',
                          borderColor: point.score ? 'transparent' : 'hairline.main',
                          transition: 'height 500ms cubic-bezier(0.32, 0.72, 0, 1)'
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.58rem' }} noWrap>
                        {point.week_key.slice(-3)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </PremiumCard>
          </Grid>

          {/* Nota de privacidad SIEMPRE visible: es lo que hace que la gente responda honesto. */}
          <Grid item xs={12}>
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ p: 2, borderRadius: '14px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.overlay' }}>
              <Lock sx={{ color: 'gold.main', fontSize: 20, flex: 'none' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('privacyLong', {
                  ns: 'wellbeing',
                  defaultValue: 'Tus respuestas son privadas. Tu empresa solo ve promedios anónimos por equipo, y los grupos de menos de 5 personas ni siquiera se muestran.'
                })}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}

export default MyWellbeing;
